import React, { Fragment, useState } from "react";
import { cn } from "../../../lib/utils";
import { Input } from "../../../Components/ui/input";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "../../../Components/ui/tabs";
import { Card, CardContent, CardHeader } from "../../../Components/ui/card";
import { ChevronDown } from "lucide-react";

interface Props {
  campaignName: string;
  fromCountry: string;
  toCountry: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  travellerCount: string;
  premiumNumbers: boolean;
  duplicateMessages: boolean;
  channels: { id: number; name: string }[];
  countryTabs: string[];
  countryNameMap: Record<string, string>;
  activeChannel: number;
  activeCountry: string;
  onSelectChannel: (id: number) => void;
  onSelectCountry: (iso: string) => void;
  configsForTab: any[];         // NOT USED for operator listing anymore, we use all translatedTemplates for the tab
  activeConfigId: number | null;
  onSelectConfig: (id: number) => void;
  // To get all operator configs for the channel:
  translatedTemplates: any[];
}

// Re-usable label + value field
const ReadOnlyField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium text-[#020617]">{label}</label>
    <Input
      value={value}
      readOnly
      className="h-10 px-3 border-0 bg-muted text-foreground/80 cursor-default"
    />
  </div>
);

const CampaignDetailsCard: React.FC<Props> = ({
  campaignName,
  fromCountry,
  toCountry,
  startDate,
  endDate,
  startTime,
  endTime,
  travellerCount,
  premiumNumbers,
  duplicateMessages,
  channels,
  countryTabs,
  countryNameMap,
  activeChannel,
  onSelectChannel,
  translatedTemplates,
}) => {
  const [openCountries, setOpenCountries] = useState<string[]>([]);

  // For the current channel, show ALL unique countries (with at least one operator config)
  const countriesForTab = translatedTemplates
    .filter((tpl) => tpl.channel_id === activeChannel)
    .map((tpl) => tpl.country_iso?.toUpperCase())
    .filter((iso, idx, arr) => iso && arr.indexOf(iso) === idx);


  // For each country, list all operator configs
  const operatorsByCountry: Record<string, any[]> = {};
  translatedTemplates
    .filter((tpl) => tpl.channel_id === activeChannel)
    .forEach((tpl) => {
      const iso = tpl.country_iso?.toUpperCase();
      if (!operatorsByCountry[iso]) operatorsByCountry[iso] = [];
      operatorsByCountry[iso].push(tpl);
    });

  // Get traveller count for each country/channel combo from your API shape:
  // function getTravellerCountForCountry(channelId: number, countryIso: string) {
  //   // This is a fallback value; you should pass the real per-country count if available
  //   // Otherwise, fallback to the overall prop
  //   return travellerCount || "0";
  // }

  // Replace this function in CampaignDetailsCard.tsx

// Get traveller count for each country/channel combo from translated_templates
function getTravellerCountForCountry(channelId: number, countryIso: string) {
  const template = translatedTemplates.find(
    (tpl) => tpl.channel_id === channelId && tpl.country_iso?.toUpperCase() === countryIso
  );
  return template?.total_traveller_count?.toString() || "0";
}

  // Calculate total travellers for this channel (sum of all countries)
  // If you have real per-country data, sum it up here
  debugger
  const totalTravellers = (countriesForTab.length > 0)
    ? countriesForTab.reduce(
      (sum, iso) => sum + Number(getTravellerCountForCountry(activeChannel, iso) || 0),
      0
    )
    : travellerCount;

  // Get channel name
  const activeChannelName = channels.find((c) => c.id === activeChannel)?.name || "";

  return (
    <div className="w-full max-w-[600px] mx-auto space-y-5 mb-[100px] text-left">
      {/* --- Details Section --- */}
      <div className="border rounded-lg bg-white p-4 space-y-5">
        <div className="text-md font-bold mb-2">Campaign details</div>
        <ReadOnlyField label="Campaign name" value={campaignName} />
        <ReadOnlyField label="Reach people from" value={fromCountry} />
        <ReadOnlyField label="Reach people traveling to" value={toCountry} />
      </div>

      {/* --- Schedule Section --- */}
      <div className="border rounded-lg bg-white p-4 space-y-5">
        <div className="text-md font-semibold mb-2">Schedule</div>
        <div className="flex gap-4">
          <div className="flex-1">
            <ReadOnlyField label="Campaign start date" value={startDate} />
          </div>
          <div className="flex-1">
            <ReadOnlyField label="Campaign end date" value={endDate} />
          </div>
        </div>
        <div className="flex gap-4">
          <div className="flex-1">
            <ReadOnlyField label="Start Time" value={startTime} />
          </div>
          <div className="flex-1">
            <ReadOnlyField label="End Time" value={endTime} />
          </div>
        </div>
        <ReadOnlyField label="Total Traveller Count" value={travellerCount} />
      </div>

      {/* --- Channel Tabs and Allocations --- */}
      <Card className="border rounded-lg">
        <CardHeader className="border-b p-0">
          <Tabs value={activeChannelName} onValueChange={(value) => {
            const channel = channels.find(c => c.name === value);
            if (channel) onSelectChannel(channel.id);
          }}>
            <TabsList className="m-3 inline-flex rounded-lg bg-muted p-1">
              {channels.map((ch, i) => (
                <React.Fragment key={ch.name}>
                  <TabsTrigger
                    value={ch.name}
                    className={cn(
                      "rounded px-3 py-1 text-sm font-medium transition-colors",
                      "data-[state=active]:bg-[#3A85F7] data-[state=active]:text-[#F8FAFC]",
                      "data-[state=inactive]:text-[#64748B]"
                    )}
                  >
                    {ch.name}
                  </TabsTrigger>
                  {i !== channels.length - 1 && (
                    <span className="mx-1 w-px self-stretch bg-black" />
                  )}
                </React.Fragment>
              ))}
            </TabsList>
          </Tabs>
        </CardHeader>

        <CardContent className="p-0">
          {countriesForTab.map((iso, idx) => {
            const isOpen = openCountries.includes(iso);
            const countryName = countryNameMap[iso] || iso;
            const operators = operatorsByCountry[iso] || [];

            return (
              <Fragment key={iso}>
                <button
                  type="button"
                  className="flex min-w-[214px] h-12 items-center justify-start px-4 py-3 text-sm font-semibold text-[#020617]"
                  onClick={() =>
                    setOpenCountries(prev =>
                      isOpen
                        ? prev.filter(c => c !== iso)
                        : [...prev, iso]
                    )
                  }
                >
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 transition-transform text-muted-foreground mt-1",
                      isOpen && "rotate-180"
                    )}
                  />
                  <span className="ml-1">
                    {countryName}
                  </span>
                </button>

                {isOpen && (
                  <div className="space-y-4 px-4 pb-6">
                    <div className="grid grid-cols-[1fr_110px] text-sm font-medium text-[#020617]">
                      <span>Operator</span>
                      <span>Traveller Count</span>
                    </div>

                    {operators.map(op => {
                      // If channel is SMS, append short_code if present
                      let displayName = op.channel_config_name;
                      // Check for SMS (assuming channel_id: 2 is SMS, or use your mapping)
                      const isSMS = activeChannel === 2;
                      if (isSMS && op.translated_template && op.translated_template.short_code) {
                        displayName = `${displayName}-${op.translated_template.short_code}`;
                      }
                      return (
                        <div key={op.channel_config_id} className="grid grid-cols-[1fr_110px] items-center gap-3">
                          <input
                            readOnly
                            value={displayName}
                            className="h-8 w-full rounded border border-[#E2E8F0] bg-gray-100 px-3 text-sm text-[#020617] cursor-not-allowed text-gray-400"
                          />
                          <input
                            readOnly
                            value={getTravellerCountForCountry(activeChannel, iso)}
                            className="h-8 w-full rounded border border-[#E2E8F0] bg-gray-100 px-3 text-sm text-[#020617] cursor-not-allowed text-gray-400"
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
                {idx !== countriesForTab.length - 1 && <div className="border-b" />}
              </Fragment>
            );
          })}
        </CardContent>

        <div className="px-6 pb-5 pt-8">
          <label className="block text-sm font-medium text-[#020617] mb-1">
            Total Traveller Count - {activeChannelName}
          </label>
          <input
            type="text"
            readOnly
            value={totalTravellers}
            className="h-10 w-full rounded border border-[#E2E8F0] bg-muted text-foreground/80 px-3 cursor-default"
          />
        </div>
      </Card>
    </div>
  );
};

export default CampaignDetailsCard;
